//system
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService, HttpServiceConfig } from './common/http.service';

//services
import { LogService } from './common/log.service';

//models
import { Address, Person, RepositoryResult } from 'src/app/models/common-models';
import { MemberBasicInfo, MemberModel, MemberDatum, CreditMemberCurrencyRequest, MemberCurrency, Registration, MemberSetting, MyDrop, MemberBalance, Member, MemberProfileSaveRequest, MemberHold } from 'src/app/models/member';
import { Discount } from '../models/meta';
import { PaymentTransactionGrid } from '../models/grids';
import { EnvironmentService } from './environment.service';
import { Action, DocumentEventType } from '../models/enums';
import { DocumentRequest, MemberDocument, MemberDocumentAgreeRequest, MemberDocumentRequest } from '../models/document';
import { MemberAddressSaveRequest } from '../models/requests';

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  
  constructor(private logService: LogService
    , private httpService: HttpService
    , private _environmentService: EnvironmentService
  ) { 
    const config: HttpServiceConfig = {
      baseUrl: `${this._environmentService.getFacilityConfig().facilityUrl}api/v${this._environmentService.getFacilityConfig().facilityApiVersion}`
    };
    this.httpService.configure(config);
  }

  getMember(): Promise<MemberModel> {
    return this.httpService.getPromise<MemberModel>(`Member/Info`);
  }

  getMemberHold(memberId: number): Observable<MemberHold | null> {
    return this.httpService.get<MemberHold>(`MemberHold/ByMember/${memberId}`).pipe(
      catchError(() => of(null)),
      // Latest hold row can be a release (Action.Remove). Only Action.Add is currently on hold.
      map(hold => hold && hold.Id > 0 && hold.Action === Action.Add ? hold : null)
    );
  }

  saveProfile(memberProfileSaveRequest: MemberProfileSaveRequest): Observable<MemberProfileSaveRequest> {
    return this.httpService.post<MemberProfileSaveRequest>(`Member/Profile`, memberProfileSaveRequest);
  }

  getMemberSetting(memberId:number, key: string): Promise<MemberSetting> {
    return this.httpService.getPromise<MemberSetting>(`MemberSetting/ByMemberAndKey/${memberId}/${key}`);
  }

  getPerson(): Promise<Person> {
    return this.httpService.getPromise<Person>(`Person/me`);
  }

  getCompeletedRegistrationSteps(): Promise<Registration> {
    return this.httpService.getPromise<Registration>(`Registration`);
  }

  addMemberDatum(memberDatumRequest: MemberDatum): Observable<MemberDatum> {
    return this.httpService.post<MemberDatum>(`MemberDatum`, memberDatumRequest);
  }

  creditMemberCurrency(creditMemberCurrencyRequest: CreditMemberCurrencyRequest): Observable<MemberCurrency> {
    return this.httpService.put<MemberCurrency>(`MemberCurrency/Credit`, creditMemberCurrencyRequest);
  }

  getMutual(memberId: number): Observable<MemberBasicInfo[]> {
    return this.httpService.get<MemberBasicInfo[]>(`MemberContact/Mutual/${memberId}/0/1000`);
  }

  getMyDrop(memberId: number): Promise<MyDrop> {
    return this.httpService.getPromise<MyDrop>(`Member/MyDrop/${memberId}`);
  }

  getBalance(memberId: number, currencyId: number): Observable<MemberBalance> {
    return this.httpService.get<MemberBalance>(`Member/Balance/${memberId}/${currencyId}`);
  }
  
  getDiscount(memberId: number): Promise<Discount> {
    return this.httpService.getPromise<Discount>(`Currency/Discount/ByMember/${memberId}`);
  }

  getPaymentTransactionsByDates(memberId: number, startDate?: string, endDate?: string): Observable<PaymentTransactionGrid[]> {
    let url = `PaymentTransaction/ByMemberAndDates/${memberId}`;
    const params: string[] = [];

    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);

    if (params.length > 0) url += `?${params.join('&')}`;

    return this.httpService.get<PaymentTransactionGrid[]>(url);
  }

  getAccessCheckinsByMemberAndDates(memberId: number, startDate: string, endDate: string): Observable<string[]> {
    // Facility API route: /Gym/Access/CheckinsByMemberAndDates/{memberId}?startDate&endDate
    const qs = `startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    return this.httpService.get<string[]>(`Gym/Access/CheckinsByMemberAndDates/${memberId}?${qs}`);
  }

  getDocumentByHookAndIntent(documentRequest: DocumentRequest): Observable<MemberDocument[]> {
    return this.httpService.post<MemberDocument[]>(`DS/Document/GetByHookAndIntent`, documentRequest);
  }

  getNonAgreedDocuments(memberDocumentRequest: MemberDocumentRequest): Observable<RepositoryResult<MemberDocument[]>> {
    return this.httpService.post<RepositoryResult<MemberDocument[]>>(`DS/MemberDocument/NonAgreed`, memberDocumentRequest);
  }

  agreeToMemberDocument(memberDocumentRequest: MemberDocumentAgreeRequest): Observable<MemberDocument[]> {
    return this.httpService.post<MemberDocument[]>(`DS/MemberDocument/Agree`, memberDocumentRequest);
  }

  changeBillingAddress(request: MemberAddressSaveRequest): Observable<Member> {
    return this.httpService.post<Member>(`Member/ChangeBillingAddress`, request);
  }
}